import { useCallback, useEffect, useState } from "react";
import { useNavigate, Outlet, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, Plus, Presentation, MoreHorizontal, Copy, Trash2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { getDotPatternStyle } from "@/components/AppBackground";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { getOnboardings, createOnboarding, deleteOnboarding, duplicateOnboarding } from "@/lib/api";

function nonEmptyString(v) {
  return typeof v === "string" && v.trim() !== "";
}

function sidebarTituloFromPreview(preview, listTitulo) {
  if (nonEmptyString(preview?.titulo)) return preview.titulo;
  return listTitulo || "Sin título";
}

function formatFecha(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}h | ${day}.${month}.${year}`;
}

function redirectIfNoOnboarding(user, navigate) {
  if (user?.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
  else if (user?.permissions?.formularios !== false) navigate("/formularios", { replace: true });
  else if (user?.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
  else if (user?.permissions?.proveedores !== false) navigate("/proveedores", { replace: true });
  else if (user?.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
  else if (user?.permissions?.metricas) navigate("/metricas", { replace: true });
  else if (user?.permissions?.correos !== false) navigate("/correos", { replace: true });
  else navigate("/", { replace: true });
}

export default function OnboardingLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const inSection = (location.pathname || "").startsWith("/onboarding");
  const { id: currentId } = useParams();
  const { user, authLoading } = useAuth();
  const settings = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewUpdates, setPreviewUpdates] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.onboarding === false) {
      redirectIfNoOnboarding(user, navigate);
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || user.permissions?.onboarding === false) return;
    if (!inSection) return;
    setLoading(true);
    getOnboardings()
      .then((data) => setItems(data.onboardings || []))
      .catch(() => {
        toast.error("Error al cargar presentaciones");
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [user, inSection]);

  const setOnboardingPreview = useCallback((onboardingId, data) => {
    setPreviewUpdates((prev) => {
      if (data == null) {
        const next = { ...prev };
        delete next[onboardingId];
        return next;
      }
      return { ...prev, [onboardingId]: { ...(prev[onboardingId] || {}), ...data } };
    });
  }, []);

  const addOnboardingToSidebarList = useCallback((item) => {
    if (!item?.id) return;
    setItems((prev) => {
      if (prev.some((o) => o.id === item.id)) return prev;
      const row = {
        id: item.id,
        titulo: item.titulo ?? "",
        fecha_creacion: item.fecha_creacion ?? new Date().toISOString(),
      };
      return [row, ...prev];
    });
  }, []);

  const removeOnboardingFromSidebarList = useCallback((onboardingId) => {
    if (!onboardingId) return;
    setItems((prev) => prev.filter((o) => o.id !== onboardingId));
    setPreviewUpdates((prev) => {
      if (!prev[onboardingId]) return prev;
      const next = { ...prev };
      delete next[onboardingId];
      return next;
    });
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const data = await createOnboarding("Nueva presentación");
      if (data?.id) {
        addOnboardingToSidebarList({
          id: data.id,
          titulo: data.titulo,
          fecha_creacion: new Date().toISOString(),
        });
        toast.success("Presentación creada");
        navigate(`/onboarding/${data.id}`);
      } else {
        toast.error("No se devolvió el id");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (onboardingId) => {
    try {
      await deleteOnboarding(onboardingId);
      removeOnboardingFromSidebarList(onboardingId);
      toast.success("Presentación eliminada");
      if (currentId === onboardingId) navigate("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleDuplicate = async (onboardingId) => {
    try {
      const data = await duplicateOnboarding(onboardingId);
      if (data?.id) {
        addOnboardingToSidebarList({
          id: data.id,
          titulo: data.titulo,
          fecha_creacion: data.fecha_creacion || new Date().toISOString(),
        });
        toast.success("Presentación duplicada");
        navigate(`/onboarding/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al duplicar");
    }
  };

  if (authLoading || !user || user.permissions?.onboarding === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const dotStyle = getDotPatternStyle(settings);
  const current = items.find((o) => o.id === currentId);
  const currentPreview = currentId ? previewUpdates[currentId] : null;
  const headerTitulo = nonEmptyString(currentPreview?.titulo)
    ? currentPreview.titulo
    : (current?.titulo || "Editar");
  const titleSuffix = currentId ? ` / ${headerTitulo}` : "";

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Onboarding" titleSuffix={titleSuffix} titleAs="h5" />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className={`shrink-0 flex flex-col border-r border-gray-300 bg-background overflow-hidden transition-[width] duration-200 ${sidebarOpen ? "w-80" : "w-12"}`}>
          <div className={`shrink-0 border-b border-gray-200 flex items-center gap-2 ${sidebarOpen ? "p-4" : "p-2 justify-center"}`}>
            {sidebarOpen && (
              <Button onClick={handleCreate} disabled={creating} className="gap-2 flex-1 min-w-0">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Plus className="w-4 h-4" strokeWidth={1.5} aria-hidden />}
                Nueva presentación
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-neutral-500 hover:text-foreground"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={sidebarOpen ? "Ocultar sidebar" : "Mostrar sidebar"}
            >
              {sidebarOpen
                ? <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />
                : <PanelLeftOpen className="w-4 h-4" strokeWidth={1.5} />}
            </Button>
          </div>
          <div className={`flex-1 overflow-auto ${sidebarOpen ? "p-4" : "p-0 overflow-hidden"}`}>
            {!sidebarOpen ? null : loading && items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin shrink-0" aria-hidden />
                <span>Cargando…</span>
              </div>
            ) : items.length === 0 ? (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle className="text-base">No hay presentaciones</CardTitle>
                  <CardDescription>Crea la primera y diseña en un lienzo con widgets y varias diapositivas.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleCreate} disabled={creating} className="gap-2">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Plus className="w-4 h-4" strokeWidth={1.5} aria-hidden />}
                    Nueva presentación
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-2 mb-4" role="list">
                {items.map((o) => {
                  const preview = previewUpdates[o.id];
                  const titulo = sidebarTituloFromPreview(preview, o.titulo);
                  const isActive = currentId === o.id;
                  return (
                    <li key={o.id}>
                      <Card className={`transition-colors shadow-none hover:bg-muted/50 ${isActive ? "bg-muted/40" : ""}`}>
                        <CardContent className="p-3 py-3">
                          <div className="flex items-center gap-1 w-full min-w-0">
                            <button
                              type="button"
                              className="flex gap-3 flex-1 min-w-0 text-left rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              onClick={() => navigate(`/onboarding/${o.id}`)}
                            >
                              <div className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center">
                                <Presentation className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                              </div>
                              <div className="min-w-0 flex-1 flex flex-col gap-0.5 justify-center">
                                <p className="text-sm font-medium truncate">{titulo || "Sin título"}</p>
                                {o.fecha_creacion && (
                                  <p className="text-xs text-muted-foreground">{formatFecha(o.fecha_creacion)}</p>
                                )}
                              </div>
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                                  aria-label="Opciones de la presentación"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                <DropdownMenuItem onClick={() => handleDuplicate(o.id)}>
                                  <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                  Duplicar
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  onClick={() => handleDelete(o.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                  Borrar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
        <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden relative" style={dotStyle}>
          <Outlet
            context={{
              setOnboardingPreview,
              addOnboardingToSidebarList,
              removeOnboardingFromSidebarList,
            }}
          />
        </div>
      </div>
    </div>
  );
}
